<template>
  <!-- eslint-disable -->
  <div class="container-fluid">
    <div class="row header">
      <div class="col-lg-10 offset-lg-1 col-xl-8 offset-xl-2">
        <p class="dg-header-label">Digital Guidelines</p>
        <h1 class="dg-header">Releases</h1>
        <p>We hope to continually improve Digital Guidelines by listening to your feedback.  Here you will find our release notes and summaries of bug fixes, new features, and other updates introduced in each release.</p>
      </div>
    </div>

    <div class="row content">
      <div class="col-lg-10 offset-lg-1 col-xl-8 offset-xl-2">
        <!-- Content -->
        <div class="row mb-5">
          <div class="col">
            <h2 class="text-primary">3.2.0</h2>
            <h3 class="h4">Bug Fixes</h3>
            <ul>
              <li>Add <span class="code">aria-hidden</span> to the icon in the <router-link to="alerts">alert component</router-link>.</li>
              <li>Fixed <router-link to="icons">icon component</router-link> to bottom align with text.</li>
              <li>Fixed tab issue when content dynamically changes within the <router-link to="modal">modal component</router-link>.</li>
              <li>Fixed animation issue on first render with the <router-link to="collapse">collapse component</router-link>.</li>
              <li>Fixed accessibility and resizing issues with the <router-link to="collapse">collapse component</router-link>.</li>
              <li>Deprecated <span class="code">refresh</span> method on the <router-link to="collapse">collapse component</router-link>.</li>
              <li>Fixed accessibility issues with the <router-link to="header">header component</router-link>.</li>
              <li>Fixed accessibility issues with the <router-link to="datepicker">date picker component</router-link>.</li>
              <li>Fixed cut off text in <span class="code">select</span> input <router-link to="forms">forms</router-link> on different browsers.</li>
              <li>Fixed loading issue with fonts.</li>
              <li>Fixed a logo resizing issue with the <router-link to="header">header component</router-link>.</li>
            </ul>
            <h3 class="h4">New Items</h3>
            <ul>
              <li>Added <router-link to="tabs">tab component</router-link> event when the active tab changes.</li>
            </ul>
          </div>
        </div>
        <div class="row mt-4">
          <div class="col">
            <h2 class="text-primary">3.1.0</h2>
            <h3 class="h4">Bug Fixes</h3>
            <ul>
              <li>Fixed issue with printing custom checkboxes and radio buttons.</li>
              <li>Fixed focus issue on the <router-link to="modal">modal component</router-link> when clicking outside a focusable element.</li>
              <li>Fixed issues with the <router-link to="collapse">collapse component</router-link> when initially closed.</li>
              <li>Fixed <router-link to="datepicker">datepicker component's</router-link> <span class="code">dateChanged</span> event.</li>
              <li>Fixed color contrast issue with inactive tabs.</li>
              <li>Fixed overflow issue on <router-link to="tabs">tabs component</router-link>.</li>
              <li>Fixed issue with the <router-link to="header">header component</router-link> where the mobile menu was rendered when no menu items were defined.</li>
              <li>Fixed focus issue on the <router-link to="datepicker">date picker component</router-link> when clicking outside a focusable element.</li>
              <li>Fixed focus issue on the <router-link to="icons">icon component</router-link>.</li>
            </ul>
            <h3 class="h4">New Items</h3>
            <ul>
              <li>Added <router-link to="toggle">toggle component</router-link>.</li>
              <li>Added <router-link to="dialog">dialog component</router-link>.</li>
              <li>Added <router-link to="cards">cards component</router-link>.</li>
              <li>Added <router-link to="card">card component</router-link>.</li>
              <li>Added print utilities.</li>
              <li>Added new properties to the <router-link to="modal">modal component</router-link> to better control how a user exits.</li>
              <li>Added <span class="code">min-date</span> and <span class="code">max-date</span> properties to the <router-link to="datepicker">datepicker component</router-link>.</li>
              <li>Added error event to the <router-link to="datepicker">datepicker component</router-link>.</li>
            </ul>
            <h3 class="h4">Changes</h3>
            <ul>
              <li>Changed all methods to return a promise instead of void.</li>
              <li>Small style updates to the <router-link to="modal">modal component</router-link>.</li>
            </ul>
          </div>
        </div>
        <div class="row mt-4">
          <div class="col">
            <h2 class="text-primary">3.0.0</h2>
            <h3 class="h4">Breaking Changes</h3>
            <ul>
              <li>Changes how the <router-link to="header">header component</router-link> handles navigation to allow more flexibility.</li>
              <li>Changed the method for passing extra links into the <router-link to="footer">footer component</router-link>.  Links now need to be wrapped in a <span class="code">som-footer-item</span> to be properly rendered.</li>
            </ul>
            <h3 class="h4">Bug Fixes</h3>
            <ul>
              <li><router to="icons">Icons</router> now default to the color of the surrounding text.</li>
              <li>Fixed setting default input date when <span class="code">selected-date</span> property is set on a <router-link to="datepicker">date picker</router-link> component.</li>
              <li>Fixed word wrap Internet Explorer issue in the <router-link to="alerts">alert component</router-link>.</li>
            </ul>
            <h3 class="h4">New Items</h3>
            <ul>
              <li>Added a new <span class="code">setMonthToOpen</span> method to <router-link to="datepicker">date picker</router-link>.</li>
              <li>Added <span class="code">content-fill</span> class to push the footer to the bottom when there isn't sufficent content to do so.</li>
              <li>Added <router-link to="badges">badge component</router-link>.</li>
              <li>Added <router-link to="buttons">button component</router-link>.</li>
              <li>Added <router-link to="panel">panel component</router-link>.</li>
            </ul>
            <h3 class="h4">Changes</h3>
            <ul>
              <li>Added skeleton UI while the <router-link to="header">header component</router-link> is loading.</li>
              <li>Modified button sizes to contain less padding.</li>
              <li>Removed custom focus ring in favor of the browser default.</li>
              <li>Changed body typography to use system fonts.</li>
            </ul>
          </div>
        </div>
        <div class="row mt-4">
          <div class="col">
            <h2 class="text-primary">2.0.1</h2>
            <h3 class="h4">Bug Fixes</h3>
            <ul>
              <li>Fixed escape key issue in <router-link to="datepicker">date picker</router-link> component.</li>
              <li>Fixed skip navigation issue in <router-link to="header">header</router-link> component.</li>
              <li>Fixed focus in <router-link to="modal">modal</router-link> component after <span class="code">refresh</span> method is called.</li>
              <li>Fixed focus issue in <router-link to="datepicker">date picker</router-link> component.</li>
            </ul>
          </div>
        </div>
        <div class="row mt-4">
          <div class="col">
            <h2 class="text-primary">2.0.0</h2>
            <h3 class="h4">Breaking Changes</h3>
            <ul>
              <li>CSS classes no longer affect <router-link to="icons">icons</router-link>.  <span class="code">Size</span> and <span class="code">Color</span> parameters were added to take care of styling.</li>
            </ul>

            <h3 class="h4">Bug Fixes</h3>
            <ul>
              <li>Fixed color contrast issues with the <span class="code">focus</span> state of <span class="code">input</span> elements.</li>
              <li>Fixed icon alignment in <router-link to="header">header</router-link> component on larger breakpoints.</li>
              <li>Fixed duplicate id issues in <router-link to="modal">modal</router-link> component.</li>
              <li>Fixed duplicate id issues in <router-link to="tabs">tabs</router-link> component.</li>
              <li>Fixed documentation around <span class="code">responsive tables</span>.</li>
              <li>Fixed inconsistencies with <router-link to="buttons">button</router-link> styles.</li>
              <li>Fixed the <span class="code">btn-link :focus</span> color.</li>
              <li>Fixed <router-link to="tabs">tab</router-link> height issue with dynamic content.</li>
            </ul>

            <h3 class="h4">New Items</h3>
            <ul>
              <li>Added the ability to dismiss an <router-link to="alerts">alert</router-link>.</li>
              <li>Added an event emitter to the <router-link to="collapse">collapse</router-link> component to better track <span class="code">aria-expanded</span> properties.</li> 
              <li>Added prop to <router-link to="collapse">collase</router-link> component to set initial state of open/closed.</li>
              <li>Added beta release of <router-link to="datepicker">date picker</router-link> component.</li>
              <li>Added ability for a custom close button in the <router-link to="modal">Modal</router-link> component.</li>
              <li>Added fixed header with scrollable content in the <router-link to="modal">Modal</router-link> component.</li>
              <li>Added <span class="code">onOpen</span> and <span class="code">onClose</span> events in the <router-link to="modal">Modal</router-link> component.</li>
              <li>Added ability to hide close button in the <router-link to="modal">Modal</router-link> component.</li>
              <li>Added <span class="code">header-accent</span> optional classes to <span class="code">h1</span>-<span class="code">h6</span> elements.</li>
              <li>Added <span class="code">refresh</span> method to the <router-link to="modal">modal</router-link> component accommodate data loaded asynchronous.</li>
              <li>Added <span class="code">btn-icon</span> class to <router-link to="buttons">buttons</router-link></li>
              <li>Added <span class="code">setActiveLink</span> method to the <router-link to="header">header</router-link> component.</li>
            </ul>

            <h3 class="h4">Changes</h3>
            <ul>
              <li><router-link to="alerts">Alerts</router-link>, <router-link to="header">Header</router-link>, and <router-link to="footer">Footer</router-link> were converted to use Shadow DOM.  The <span class="code">som-components.css</span> is no longer needed to render those components.</li>
              <li>Updated gray-100 through gray-400 <router-link to="colors">colors</router-link> to remove the blue hue</li>
              <li>Modified <span class="code">h1</span>-<span class="code">h6</span> heading sizes.</li>
              <li>Updated <router-link to="typography">typography</router-link> documentation to be more clear and add more accessibility information.</li>
              <li>Updated the <router-link to="header">header</router-link> navigation font.  Adjusted the size and used the header default of Monteserrat</li>
              <li>Updated the border on <router-link to="cards">cards</router-link> and <router-link to="tables">tables</router-link> to <span class="code">gray-200</span></li>
              <li>Moved the <router-link to="alerts">alerts</router-link> description to be next to the alert icon instead of below</li>
              <li>Removed <span class="code">font-weight-light</span> class.</li>
              <li>Removed Google Fonts dependency and moved to self hosting fonts.</li>
              <li>Updated <router-link to="modal">modal</router-link> background color to <span class="code">transparent</span></li>
              <li>Updated the <span class="code">focus</span> color on all focusable elements</li>
            </ul>
          </div>
        </div>

        <div class="row mt-4">
          <div class="col">
            <h2 class="text-primary">1.0.0</h2>
            <h3 class="h4">Bug Fixes</h3>
            <ul>
              <li>Removed menu icon when there are no primary navigation items.</li>
              <li>Fixed issue in header component where responsive secondary navigation wouldn't show if a primary navigation wasn't marked as <span class="code">class="active"</span>.</li>
              <li>Fixed header component documentation regarding primary and secondary navigation.</li>
              <li>Fixed CSS and JS references in <router-link to="page-templates">Page Template</router-link></li>
              <li>Fixed issue with the <span class="code">Skip Navigation</span> in the <router-link to="header">header component</router-link></li>
              <li>Renamed <router-link to="alerts">alert component</router-link> <span class="code">title</span> property to <span class="code">headline</span> to not conflict with the native HTML property.</li>
              <li>Fixed ARIA landmark issues on documentation website</li>
              <li>Fixed screenreader focus point on route change</li>
              <li>Cleaned up color properties and removed unused colors - this breaks the previous syntax for the <router-link to="alerts">alert component</router-link></li>
            </ul>

            <h3 class="h4">New Items</h3>
            <ul>
              <li>Added the beta release of a <router-link to="modal">modal component</router-link>.</li>
              <li>Added <span class="code">target="_blank"</span> support to the <router-link to="footer">footer component</router-link>.</li>
            </ul>
          </div>
        </div>

        <div class="row mt-4">
          <div class="col">
            <h2 class="text-primary">1.0.0-beta</h2>
            <p>This is our initial release of Digital Guidelines 🚀</p>
            <ul>
              <li>Released custom web components for header, footer, tabs, and collapse.</li>
              <li>Forked and themed <a href="https://getbootstrap.com/docs/4.0/getting-started/introduction/" target="_blank" rel="noopener noreferrer">Bootstrap 4</a> to match the State of Michigan branding.</li>
            </ul>

            <p>Thank You</p>
            <ul>
              <li><a class="font-weight-bold" href="https://designsystem.digital.gov" target="_blank" rel="noopener noreferrer">18F's Design System</a> : This vision for this project is largely based on the work done at 18F.  They did such a good job that we lifted a lot of content around accessibility from them.</li>
              <li><a class="font-weight-bold" href="https://getbootstrap.com/docs/4.0/getting-started/introduction/" target="_blank" rel="noopener noreferrer">Bootstrap 4</a> : The base from which Digital Guidelines was build on.</li>
              <li><a class="font-weight-bold" href="https://stenciljs.com/" target="_blank" rel="noopener noreferrer">StencilJS</a> : Used for all of our web components.  We cannot say enough about the how helpful this has been.  We have great plans to continue making components and we're excited to see what comes next.</li>
              <li><span class="font-weight-bold">Suzanne Pauley</span> for helping make this vision a reality.</li>
            </ul>
          </div>
        </div>
        <!-- Content End -->
      </div>
    </div>
  </div>  
  <!-- eslint-enable -->
</template>

<script>
export default {
  name: "Releases"
};
</script>

/* esling-disable */
<style lang="sass?outputStyle=expanded">
</style>
/* eslint-enable */



// WEBPACK FOOTER //
// src/components/routes/Releases.vue